import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useIsFetching, useQueryClient } from '@tanstack/react-query'
import { fromCoreHistoryToRechartDataTransformer } from '../transformers'
import { CoreHistoryChartData } from '../models/history-data'
import { fetchCoreHistoryDataBasedOnView, HistoryPayloadBasedOnView } from '../api'
import { Resolution } from 'src/Libs/chart-kit/shared/types'
import { usePortalQuery } from 'src/Libs/api'

interface ReturnType {
  historyCoreData: CoreHistoryChartData[]
  isLoading: boolean
}

const ONE_DAY_MS = 1000 * 60 * 60 * 24
const FIFTEEN_MINUTES_MS = 1000 * 60 * 15

/**
 * This hook fetches history data. It uses a history step route which is an alternative to the default history route.
 * The route has the potential to be more performant.
 *
 * TODO: BatChargeLevel is returned but not used here / not part of the HistoryData
 *
 * @param storageId
 * @param payload
 * @returns
 */
export const useFetchCoreHistoryViewData = (storageId: string, payload: HistoryPayloadBasedOnView, resolution?: Resolution): ReturnType => {
  const authFetch = useAuthFetchWithResponse()
  const { view } = payload

  const isTodayInRange = (to: number): boolean => {
    const today = new Date()
    const toDate = new Date(to)
    return today.toDateString() === toDate.toDateString()
  }

  const staleTime = isTodayInRange(payload.to) ? FIFTEEN_MINUTES_MS : ONE_DAY_MS

  const { data, isLoading } = usePortalQuery({
    queryKey: ['core-history-data', storageId, payload],
    queryFn: () => fetchCoreHistoryDataBasedOnView(storageId, authFetch, payload),
    enabled: !!storageId,
    refetchOnWindowFocus: true,
    staleTime,
  })

  const factor = view === 'day' || resolution === 'hour' || resolution === '15min' ? 4 : 1
  const dataTimeStampCorrection: CoreHistoryChartData[] = fromCoreHistoryToRechartDataTransformer(data ?? [], factor)

  return { historyCoreData: dataTimeStampCorrection, isLoading }
}

interface ReturnTypeFetchManual {
  handleFetchingCoreHistoryData: (resolution: Resolution, payload: HistoryPayloadBasedOnView) => Promise<{ data: CoreHistoryChartData[] }>
  isFetching: boolean
}

/**
 * This hook is an alternative to useFetchCoreHistoryViewData.
 * Instead of fetching automatically this hook provides a function to fetch manually.
 *
 * @param storageId
 * @returns
 */
export const useFetchCoreHistoryViewDataManual = (storageId: string): ReturnTypeFetchManual => {
  const authFetch = useAuthFetchWithResponse()
  const queryClient = useQueryClient()
  const isFetching = useIsFetching({ queryKey: ['core-history-data-manual', storageId] }) > 0

  const handleFetchingCoreHistoryData = async (
    resolution: Resolution,
    payload: HistoryPayloadBasedOnView
  ): Promise<{ data: CoreHistoryChartData[] }> => {
    const queryKey = ['core-history-data-manual', storageId, payload]

    const data = await queryClient.fetchQuery({
      queryKey,
      queryFn: () => fetchCoreHistoryDataBasedOnView(storageId, authFetch, payload),
    })

    const factor = resolution === '15min' ? 4 : 1

    const dataTimeStampCorrection: CoreHistoryChartData[] = fromCoreHistoryToRechartDataTransformer(data ?? [], factor)

    return { data: dataTimeStampCorrection }
  }

  return { handleFetchingCoreHistoryData, isFetching }
}
